import { useState } from 'react';
import PropTypes from 'prop-types';

import * as yup from 'yup';

import { trans } from '@spotahome/soyuz/client';

import { useReCaptcha } from '../../../context/RecaptchaContext';

import FormProvider from '../../FormProvider';
import CheckboxWithLabel from '../../CheckboxWithLabel';
import Button from '../../Button';

import LoginSignupModalTermsAndConditions from '../components/LoginSignupModalTermsAndConditions';
import LoginSignupModalToggle from '../components/LoginSignupModalToggle';

import { LOGIN, SIGNUP } from '../constants';

import styles from './SignupForm.module.scss';

const formSchema = yup.object().shape({
  email: yup
    .string()
    .ensure()
    .required('common.form.required-field')
    .email('lyp.validation.email.format')
});

const formSchemaStep2 = yup.object().shape({
  name: yup.string().ensure().required('common.form.required-field'),
  password: yup.string().ensure().required('common.form.required-field')
});

const SignupForm = ({
  onToggleAction,
  onSubmit,
  error = null,
  disableLoginSignUpToggle = false,
  initialValues,
  currentStep,
  onNextStep,
  onPrevStep,
  clearError
}) => {
  const [isOptInAccepted, setOptInAccepted] = useState(false);
  const [formValues, setFormValues] = useState(initialValues);
  const { executeRecaptcha } = useReCaptcha('signup');

  const handleToggleAction = () => {
    onToggleAction(LOGIN);
  };

  const handleOptInChange = value => {
    setOptInAccepted(value);
  };

  const handlePrevStep = () => {
    clearError();
    onPrevStep();
  };

  const handleNextStep = payload => {
    clearError();
    setFormValues(prevValues => ({
      ...prevValues,
      marketingOptIn: isOptInAccepted,
      ...payload
    }));
    onNextStep();
  };

  const handleFormSubmit = async payload => {
    const recaptchaToken = await executeRecaptcha();
    await onSubmit({ ...formValues, ...payload }, recaptchaToken);
  };

  return currentStep === 0 ? (
    <FormProvider
      initialValues={formValues}
      onSubmit={handleNextStep}
      schema={formSchema}
      fieldsClassName={styles['signup-form__field']}
      key="fp-step-1"
    >
      <FormProvider.Input
        type="email"
        name="email"
        title={trans('loginsignup.modal.email')}
        required
        dataTest="loginSignupModalEmail"
      />

      <CheckboxWithLabel
        checked={isOptInAccepted}
        callback={handleOptInChange}
        text={trans('opt-in-checkbox-text')}
        name="marketingOptIn"
        id="marketingOptIn"
        className={styles['signup-form__opt-in-checkbox']}
      />

      <div className={styles['signup-form__footer']}>
        <span className={styles['signup-form__continue']}>
          <FormProvider.Submit
            fieldsClassName={styles['signup-form__submit']}
            dataTest="loginSignupModal_step1_cta"
          >
            {trans('loginsignup.modal.continue')}
          </FormProvider.Submit>
        </span>
        <span className={styles['signup-form__terms-and-conditions']}>
          <LoginSignupModalTermsAndConditions action={SIGNUP} />
        </span>
        {!disableLoginSignUpToggle && (
          <span className={styles['signup-form__toggle']}>
            <LoginSignupModalToggle
              action={SIGNUP}
              onToggleAction={handleToggleAction}
            />
          </span>
        )}
      </div>
    </FormProvider>
  ) : (
    <FormProvider
      initialValues={formValues}
      onSubmit={handleFormSubmit}
      schema={formSchemaStep2}
      fieldsClassName={styles['signup-form__field']}
      key="fp-step-2"
      onBlur={clearError}
    >
      <FormProvider.Input
        type="text"
        name="name"
        title={trans('loginsignup.modal.name')}
        required
        dataTest="loginSignupModalName"
        onBlurHandler={clearError}
      />

      <FormProvider.Password
        name="password"
        title={trans('loginsignup.modal.password')}
        required
        dataTest="loginSignupModalPassword"
        onBlurHandler={clearError}
      />

      <div className={styles['signup-form__footer']}>
        <LoginSignupModalTermsAndConditions action={SIGNUP} />
        <div className={styles['signup-form__actions']}>
          <Button
            className={styles['signup-form__go-back']}
            onClick={handlePrevStep}
            color="link"
            dataTest="loginSignupModal_step2_goBack"
          >
            {trans('loginsignup.modal.signup-form.go-back')}
          </Button>

          <FormProvider.Submit
            fieldsClassName={styles['signup-form__submit']}
            dataTest="loginSignupModal_step2_cta"
          >
            {trans('loginsignup.modal.signup-form.submit')}
          </FormProvider.Submit>
        </div>
      </div>

      {!!error && (
        <p
          data-test="signup-form-error"
          className={styles['signup-form__error']}
        >
          {error}
        </p>
      )}
    </FormProvider>
  );
};

SignupForm.propTypes = {
  onToggleAction: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onNextStep: PropTypes.func.isRequired,
  onPrevStep: PropTypes.func.isRequired,
  error: PropTypes.string,
  disableLoginSignUpToggle: PropTypes.bool,
  initialValues: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    password: PropTypes.string
  }).isRequired,
  currentStep: PropTypes.number.isRequired,
  clearError: PropTypes.func.isRequired
};

export default SignupForm;
